import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import compact from 'lodash/compact';
import filter from 'lodash/filter';
import find from 'lodash/find';
import maxBy from 'lodash/maxBy';
import map from 'lodash/map';
import uniq from 'lodash/uniq';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { openConfirmDialog } from '../../../../store/dialogs';
import { putDemandOrganization, putRTO, DEMAND_APP_ID } from '../../../../store/demand';
import OrganizationDialog from '../../../../components/Dialogs/OrganizationDialog';
import ConnectorSwitch from '../ConnectorSwitch';

dayjs.extend(relativeTime);

// ─── helpers ──────────────────────────────────────────────────────────────────

const STATUS_CONFIG = {
  healthy: { label: 'Healthy', color: 'success', variant: 'outlined' },
  'no-sites': { label: 'No Sites', color: 'default', variant: 'filled' },
  'no-members': { label: 'No Members', color: 'warning', variant: 'filled' },
  'connector-down': { label: 'Connector Down', color: 'error', variant: 'filled' },
};

function getOrgStatus(org, orgSites, orgMembers, niagara) {
  if (org.is_portfolio) return 'healthy';
  if (!orgSites.length) return 'no-sites';
  if (!orgMembers.length) return 'no-members';
  if (niagara && !niagara.active) return 'connector-down';
  return 'healthy';
}

function lastSeenFromMeters(orgMeters) {
  const m = maxBy(orgMeters, (m) => m?.stats?.last_ok_time);
  return m?.stats?.last_ok_time ?? null;
}

function LastSeen({ ts }) {
  if (ts == null)
    return <Typography variant='caption' color='text.disabled'>Never</Typography>;
  const stale = dayjs().diff(dayjs.unix(ts), 'hour') > 2;
  return (
    <Tooltip title={dayjs.unix(ts).format('MMM D, YYYY h:mm a')}>
      <Typography
        variant='caption'
        color={stale ? 'warning.main' : 'text.secondary'}
        fontWeight={stale ? 600 : 400}>
        {dayjs.unix(ts).fromNow()}
      </Typography>
    </Tooltip>
  );
}

// ─── StatCard ─────────────────────────────────────────────────────────────────

function StatCard({ label, value, subvalue, valueColor }) {
  return (
    <Card variant='outlined' sx={{ flex: 1, minWidth: 130 }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography
          variant='caption'
          color='text.secondary'
          sx={{
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            fontWeight: 600,
            fontSize: 10,
            display: 'block',
            mb: 0.5,
          }}>
          {label}
        </Typography>
        <Typography
          variant='h5'
          fontWeight={700}
          color={valueColor ?? 'text.primary'}
          sx={{ lineHeight: 1 }}>
          {value}
        </Typography>
        {subvalue && (
          <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5, display: 'block' }}>
            {subvalue}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// ─── GridCard ─────────────────────────────────────────────────────────────────

function GridCard({ rto, onToggle }) {
  return (
    <Card variant='outlined' sx={{ minWidth: 220 }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 1 }}>
          <Typography variant='subtitle1' fontWeight={700}>
            {rto.rto_id}
          </Typography>
          <Chip
            size='small'
            label={rto.shed ? 'Shed Active' : 'Normal'}
            color={rto.shed ? 'error' : 'success'}
          />
        </Stack>
        <Typography variant='caption' color='text.secondary' display='block' sx={{ mb: 1.5 }}>
          Peak threshold: {rto.peak_percent != null ? `${rto.peak_percent}%` : '—'}
        </Typography>
        <Button
          variant={rto.shed ? 'outlined' : 'contained'}
          color={rto.shed ? 'inherit' : 'error'}
          size='small'
          fullWidth
          onClick={() => onToggle(rto)}>
          {rto.shed ? 'Clear Shed Signal' : 'Activate Shed Signal'}
        </Button>
      </CardContent>
    </Card>
  );
}

// ─── OrgDrawer ────────────────────────────────────────────────────────────────

function OrgDrawer({ org, sites, meters, memberships, niagaras, users, onClose }) {
  const dispatch = useDispatch();
  const orgSites = useMemo(() => filter(sites, { org_id: org?.org_id }), [sites, org]);
  const orgMembers = useMemo(() => filter(memberships, { org_id: org?.org_id }), [memberships, org]);
  const niagara = useMemo(() => find(niagaras, { org_id: org?.org_id }), [niagaras, org]);

  const handleToggleSuppressEmails = (e) => {
    dispatch(
      putDemandOrganization({
        ...org,
        suppress_pjm_load_shed_emails: e.target.checked,
      })
    );
  };

  const siteRows = useMemo(
    () =>
      orgSites.map((site) => {
        const siteMeters = filter(meters, { site_id: site.site_id });
        return {
          site,
          meterCount: siteMeters.length,
          lastSeen: lastSeenFromMeters(siteMeters),
        };
      }),
    [orgSites, meters]
  );

  const memberRows = useMemo(
    () =>
      orgMembers.map((m) => {
        const user = find(users, (u) => u.username === m.user_id || u.user_id === m.user_id);
        return { membership: m, user };
      }),
    [orgMembers, users]
  );

  return (
    <Box sx={{ width: 420, p: 3, overflowY: 'auto' }}>
      {/* Header */}
      <Stack direction='row' alignItems='flex-start' justifyContent='space-between' sx={{ mb: 2 }}>
        <Box>
          <Typography variant='h6' fontWeight={700} sx={{ lineHeight: 1.2 }}>
            {org?.name}
          </Typography>
          <Typography variant='caption' color='text.secondary' sx={{ fontFamily: 'monospace' }}>
            {org?.org_id}
          </Typography>
        </Box>
        <IconButton size='small' onClick={onClose}>
          <FontAwesomeIcon icon={['fal', 'xmark']} />
        </IconButton>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      {/* Settings */}
      <Typography variant='subtitle2' fontWeight={600} sx={{ mb: 1 }}>
        Settings
      </Typography>
      <FormControlLabel
        sx={{ mb: 2, ml: 0 }}
        control={
          <Switch
            size='small'
            checked={!!org?.suppress_pjm_load_shed_emails}
            onChange={handleToggleSuppressEmails}
          />
        }
        label={
          <Typography variant='body2'>Suppress PJM load shed emails</Typography>
        }
      />
      <Divider sx={{ mb: 2 }} />

      {/* Connector */}
      {niagara && (
        <>
          <Typography variant='subtitle2' fontWeight={600} sx={{ mb: 1 }}>
            Connector
          </Typography>
          <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 2 }}>
            <ConnectorSwitch niagara={niagara} orgName={org?.name} />
            <Chip
              size='small'
              label={niagara.active ? 'Active' : 'Inactive'}
              color={niagara.active ? 'success' : 'default'}
              variant='outlined'
            />
            {niagara.devices?.length > 0 && (
              <Typography variant='caption' color='text.secondary'>
                {niagara.devices.length} device{niagara.devices.length !== 1 ? 's' : ''}
              </Typography>
            )}
          </Stack>
          <Divider sx={{ mb: 2 }} />
        </>
      )}

      {/* Sites */}
      <Typography variant='subtitle2' fontWeight={600} sx={{ mb: 1 }}>
        Sites ({siteRows.length})
      </Typography>
      {siteRows.length === 0 ? (
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          No sites configured.
        </Typography>
      ) : (
        <Table size='small' sx={{ mb: 2 }}>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell align='center'>Meters</TableCell>
              <TableCell>Last Seen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {map(siteRows, ({ site, meterCount, lastSeen }) => (
              <TableRow key={site.site_id}>
                <TableCell>
                  <Typography variant='body2' fontWeight={500}>
                    {site.name}
                  </Typography>
                </TableCell>
                <TableCell align='center'>
                  <Typography variant='body2'>{meterCount}</Typography>
                </TableCell>
                <TableCell>
                  <LastSeen ts={lastSeen} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Divider sx={{ mb: 2 }} />

      {/* Members */}
      <Typography variant='subtitle2' fontWeight={600} sx={{ mb: 1 }}>
        Members ({memberRows.length})
      </Typography>
      {memberRows.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          No members.
        </Typography>
      ) : (
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>User</TableCell>
              <TableCell>Role</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {map(memberRows, ({ membership, user }) => {
              const displayName =
                user
                  ? [user.given_name, user.family_name].filter(Boolean).join(' ') || user.email || user.username
                  : membership.user_id;
              return (
                <TableRow key={membership.user_id}>
                  <TableCell>
                    <Typography variant='body2' fontWeight={500}>
                      {displayName}
                    </Typography>
                    {user?.email && displayName !== user.email && (
                      <Typography variant='caption' color='text.secondary' display='block'>
                        {user.email}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size='small'
                      label={membership.role || 'member'}
                      variant='outlined'
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}

// ─── Console ──────────────────────────────────────────────────────────────────

export default function Console() {
  const dispatch = useDispatch();
  const { organizations, sites, meters, memberships, niagaras, rtos, users } =
    useSelector((state) => state.demand);

  const [drawerOrgId, setDrawerOrgId] = useState(null);
  const [search, setSearch] = useState('');
  const [orgDialogOpen, setOrgDialogOpen] = useState(false);

  const drawerOrg = useMemo(
    () => find(organizations, { org_id: drawerOrgId }),
    [organizations, drawerOrgId]
  );

  const nonPortfolioOrgs = useMemo(
    () => filter(organizations, (o) => !o.is_portfolio),
    [organizations]
  );

  const activeShedMeters = useMemo(
    () => filter(meters, (m) => m.shed === true),
    [meters]
  );

  const gridSignalActive = useMemo(() => rtos.some((r) => r.shed), [rtos]);

  const orgRows = useMemo(
    () =>
      nonPortfolioOrgs.map((org) => {
        const orgSites = filter(sites, { org_id: org.org_id });
        const orgMeters = filter(meters, { org_id: org.org_id });
        const orgMembers = filter(memberships, { org_id: org.org_id });
        const niagara = find(niagaras, { org_id: org.org_id });
        const shedCount = filter(orgMeters, { shed: true }).length;
        const rtoIds = uniq(compact(orgMeters.map((m) => m.rto_id)));
        return {
          org,
          siteCount: orgSites.length,
          meterCount: orgMeters.length,
          memberCount: orgMembers.length,
          niagara,
          lastSeen: lastSeenFromMeters(orgMeters),
          shedCount,
          rtoIds,
          status: getOrgStatus(org, orgSites, orgMembers, niagara),
        };
      }),
    [nonPortfolioOrgs, sites, meters, memberships, niagaras]
  );

  const filteredRows = useMemo(() => {
    if (!search.trim()) return orgRows;
    const q = search.toLowerCase();
    return orgRows.filter((r) => r.org.name?.toLowerCase().includes(q));
  }, [orgRows, search]);

  const handleToggleShed = (rto) => {
    const next = !rto.shed;
    dispatch(
      openConfirmDialog({
        title: `${next ? 'Activate' : 'Clear'} ${rto.rto_id} Shed Signal`,
        message: (
          <p>
            Are you sure you want to <strong>{next ? 'activate' : 'clear'}</strong> the
            grid load shed signal for <strong>{rto.rto_id}</strong>?
            {next && ' This will command all enrolled sites to shed load.'}
          </p>
        ),
        onConfirm: () => dispatch(putRTO({ ...rto, shed: next })),
      })
    );
  };

  return (
    <>
      <Box sx={{ maxWidth: 1100, width: '100%', px: 1, mt: 1, mb: 5 }}>

        {/* ── KPI row ──────────────────────────────────────────────────── */}
        <Stack direction='row' spacing={1.5} sx={{ mb: 3 }} flexWrap='wrap'>
          <StatCard label='Enrolled Orgs' value={nonPortfolioOrgs.length} />
          <StatCard label='Sites' value={sites.length} />
          <StatCard label='Meters' value={meters.length} />
          <StatCard
            label='Grid Signal'
            value={gridSignalActive ? 'Active' : 'Inactive'}
            valueColor={gridSignalActive ? 'error.main' : 'success.main'}
            subvalue={
              activeShedMeters.length > 0
                ? `${activeShedMeters.length} meter${activeShedMeters.length !== 1 ? 's' : ''} shedding`
                : undefined
            }
          />
        </Stack>

        {/* ── Grids ────────────────────────────────────────────────────── */}
        <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 1 }}>
          Grids
        </Typography>
        <Stack direction='row' spacing={1.5} flexWrap='wrap' sx={{ mb: 3 }}>
          {map(rtos, (rto) => (
            <GridCard key={rto.rto_id} rto={rto} onToggle={handleToggleShed} />
          ))}
          {rtos.length === 0 && (
            <Typography variant='body2' color='text.secondary'>
              No grids configured.
            </Typography>
          )}
        </Stack>

        {/* ── Active Dispatches ─────────────────────────────────────────── */}
        {activeShedMeters.length > 0 && (
          <>
            <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 1 }}>
              <Typography variant='subtitle1' fontWeight={600}>
                Active Dispatches
              </Typography>
              <Chip size='small' label={activeShedMeters.length} color='error' />
            </Stack>
            <Card variant='outlined' sx={{ mb: 3 }}>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Meter</TableCell>
                    <TableCell>Organization</TableCell>
                    <TableCell>Site</TableCell>
                    <TableCell>RTO</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {map(activeShedMeters, (meter) => {
                    const org = find(organizations, { org_id: meter.org_id });
                    const site = find(sites, { site_id: meter.site_id });
                    return (
                      <TableRow key={meter.meter_id} hover>
                        <TableCell>
                          <Typography variant='body2' fontWeight={500}>
                            {meter.name || meter.meter_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant='body2'>
                            {org?.name || meter.org_id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant='body2' color='text.secondary'>
                            {site?.name || meter.site_id || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip size='small' label={meter.rto_id} variant='outlined' />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          </>
        )}

        {/* ── Organizations ─────────────────────────────────────────────── */}
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          sx={{ mb: 1.5 }}>
          <Typography variant='subtitle1' fontWeight={600}>
            Organizations
          </Typography>
          <Stack direction='row' spacing={1} alignItems='center'>
            <TextField
              size='small'
              placeholder='Search…'
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: 200 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position='start'>
                      <FontAwesomeIcon icon={['fal', 'magnifying-glass']} fontSize={14} />
                    </InputAdornment>
                  ),
                  endAdornment: search ? (
                    <InputAdornment position='end'>
                      <IconButton size='small' onClick={() => setSearch('')}>
                        <FontAwesomeIcon icon={['fal', 'xmark']} fontSize={12} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                },
              }}
            />
            <Button
              variant='contained'
              size='small'
              startIcon={<FontAwesomeIcon icon={['fal', 'plus']} fontSize={12} />}
              onClick={() => setOrgDialogOpen(true)}>
              Add Org
            </Button>
          </Stack>
        </Stack>
        <Card variant='outlined'>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell align='center'>Sites</TableCell>
                <TableCell align='center'>Meters</TableCell>
                <TableCell align='center'>Members</TableCell>
                <TableCell>RTO</TableCell>
                <TableCell>Connector</TableCell>
                <TableCell>Last Seen</TableCell>
                <TableCell>Status</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map(
                ({ org, siteCount, meterCount, memberCount, niagara, lastSeen, shedCount, rtoIds, status }) => {
                  const sc = STATUS_CONFIG[status];
                  return (
                    <TableRow key={org.org_id} hover>
                      <TableCell>
                        <Typography variant='body2' fontWeight={500}>
                          {org.name}
                        </Typography>
                      </TableCell>
                      <TableCell align='center'>
                        <Typography variant='body2'>{siteCount}</Typography>
                      </TableCell>
                      <TableCell align='center'>
                        <Typography variant='body2'>{meterCount}</Typography>
                      </TableCell>
                      <TableCell align='center'>
                        <Typography variant='body2'>{memberCount}</Typography>
                      </TableCell>
                      <TableCell>
                        {rtoIds.length > 0 ? (
                          <Stack direction='row' spacing={0.5}>
                            {rtoIds.map((rtoId) => (
                              <Chip key={rtoId} size='small' label={rtoId} variant='outlined' />
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant='caption' color='text.disabled'>—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {niagara ? (
                          <Chip
                            size='small'
                            label={niagara.active ? 'Active' : 'Inactive'}
                            color={niagara.active ? 'success' : 'default'}
                            variant='outlined'
                          />
                        ) : (
                          <Typography variant='caption' color='text.disabled'>—</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <LastSeen ts={lastSeen} />
                      </TableCell>
                      <TableCell>
                        <Stack direction='row' spacing={0.5} alignItems='center'>
                          <Chip
                            size='small'
                            label={sc.label}
                            color={sc.color}
                            variant={sc.variant}
                          />
                          {shedCount > 0 && (
                            <Chip size='small' label={`${shedCount} shedding`} color='error' />
                          )}
                        </Stack>
                      </TableCell>
                      <TableCell align='right'>
                        <Button
                          size='small'
                          variant='outlined'
                          onClick={() => setDrawerOrgId(org.org_id)}>
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                }
              )}
              {filteredRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9}>
                    <Typography variant='body2' color='text.secondary' sx={{ py: 1 }}>
                      {search ? 'No organizations match your search.' : 'No organizations found.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      </Box>

      {/* ── Org detail drawer ─────────────────────────────────────────── */}
      <Drawer anchor='right' open={drawerOrgId !== null} onClose={() => setDrawerOrgId(null)}>
        {drawerOrg && (
          <OrgDrawer
            org={drawerOrg}
            sites={sites}
            meters={meters}
            memberships={memberships}
            niagaras={niagaras}
            users={users}
            onClose={() => setDrawerOrgId(null)}
          />
        )}
      </Drawer>

      {/* ── Add org dialog ────────────────────────────────────────────── */}
      <OrganizationDialog
        appId={DEMAND_APP_ID}
        type='organization'
        open={orgDialogOpen}
        closeDialog={() => setOrgDialogOpen(false)}
      />
    </>
  );
}